// Master-only Tenants page.
//
// P1-6 (Agent B): replace the read-only list with full CRUD.
//   - Create tenant modal
//   - Edit tenant modal
//   - Suspend / restore / archive with confirm dialogs
//   - Tenant health summary visible inline
//   - Tenant view flow (preserved from previous tranche)
// Inactive tenants render disabled in the tenant-view selector column.

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Icon, Spinner } from '@ng-unifi/ui';
import { apiClient, ApiError } from '../lib/api.js';
import { rowMenuStyle } from '../lib/rowMenuStyle.js';

interface TenantRow {
  id: string;
  name: string;
  contact_email: string | null;
  contact_name?: string | null;
  contact_phone?: string | null;
  notes?: string | null;
  status: 'active' | 'suspended' | 'archived';
  created_at: string;
  employee_count?: number;
  fob_count?: number;
  pin_count?: number;
}

const STATUS_TONE: Record<TenantRow['status'], string> = {
  active: 'bg-secondary-container text-on-secondary-container',
  suspended: 'bg-tertiary-fixed text-on-tertiary-fixed',
  archived: 'bg-surface-container-high text-on-surface-variant',
};

async function loadTenants(includeArchived: boolean): Promise<TenantRow[]> {
  const res = await apiClient.get<{ data: TenantRow[] }>(`/api/v1/tenants${includeArchived ? '?include_archived=true' : ''}`, {
    skipTenantScope: true,
  });
  return res.data;
}

type ModalKind = 'create' | 'edit' | 'suspend' | 'restore' | 'archive';

export function TenantsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<TenantRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [modal, setModal] = useState<{
    kind: ModalKind;
    tenant: TenantRow | null;
  } | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  // UX-014: TenantDetailPage's "Edit" navigated here with ?focus=<id>, which
  // nothing read — the operator pressed Edit and got an unchanged list. Editing
  // lives in this page's modal, so honour the ?edit=<id> convention already
  // used by EmployeeDetailPage and strip the parameter once consumed.
  useEffect(() => {
    if (!rows) return;
    const wanted = searchParams.get('edit');
    if (!wanted) return;
    const tenant = rows.find((r) => r.id === wanted);
    if (tenant) setModal({ kind: 'edit', tenant });
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
  }, [rows, searchParams, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    loadTenants(showArchived)
      .then((d) => {
        if (cancelled) return;
        setRows(d);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load');
      });
    return () => {
      cancelled = true;
    };
  }, [reloadTick, showArchived]);

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-container text-on-primary">
            <Icon name="apartment" className="text-2xl" />
          </div>
          <div>
            <h1 className="font-headline-md text-headline-md text-on-surface">Tenants</h1>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Business tenants sharing this controller. Master admins only. Choose a tenant to edit
              its properties, employees, and access.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setModal({ kind: 'create', tenant: null })}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary font-label-md text-label-md rounded-lg hover:bg-opacity-90 transition-all"
        >
          <Icon name="add" />
          <span>Create tenant</span>
        </button>
      </header>

      {toast ? (
        <div
          role="status"
          className="bg-secondary-container text-on-secondary-container rounded-lg px-4 py-2 font-body-md text-body-md flex items-center gap-2"
        >
          <Icon name="check_circle" />
          <span className="flex-1">{toast}</span>
          <button type="button" onClick={() => setToast(null)} className="underline font-medium">
            Dismiss
          </button>
        </div>
      ) : null}

      <label className="inline-flex min-h-6 items-center gap-2 font-body-md text-body-md text-on-surface">
        <input type="checkbox" checked={showArchived} onChange={(event) => setShowArchived(event.target.checked)} />
        Show archived tenants
      </label>

      <section className="bg-surface-container-lowest border border-outline-variant rounded-xl overflow-hidden">
        {error ? (
          <div className="p-6 text-error font-body-md text-body-md" role="alert">
            {error}
          </div>
        ) : rows == null ? (
          <div className="p-12 flex justify-center">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-on-surface-variant font-body-md text-body-md">
            No tenants yet. Use the Create tenant button to add one.
          </div>
        ) : (
          <>
            {/* Mobile card stack */}
            <div className="md:hidden divide-y divide-outline-variant">
              {rows.map((t) => (
                <div
                  key={t.id}
                  className="px-4 py-4 space-y-3 transition-colors hover:bg-surface-container-low"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <Link
                        to={`/tenants/${t.id}`}
                        className="rounded-sm font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        data-testid={`tenant-row-link-${t.id}`}
                      >
                        {t.name}
                      </Link>
                      {t.contact_email ? (
                        <p className="text-sm text-on-surface-variant break-all">
                          {t.contact_email}
                        </p>
                      ) : (
                        <p className="text-sm text-on-surface-variant">No contact email</p>
                      )}
                    </div>
                    <RowMenu tenant={t} onAction={(kind) => setModal({ kind, tenant: t })} />
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md uppercase ${STATUS_TONE[t.status]}`}
                    >
                      {t.status}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-3 text-sm text-on-surface-variant">
                    <span>{t.employee_count ?? 0} employees</span>
                    <span>{t.fob_count ?? 0} fobs</span>
                    <span>{t.pin_count ?? 0} PINs</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-container">
                  <tr>
                    <Th width="22%">Name</Th>
                    <Th>Contact</Th>
                    <Th width="12%">Status</Th>
                    <Th width="8%">Employees</Th>
                    <Th width="8%">Fobs</Th>
                    <Th width="8%">PINs</Th>
                    <Th width="14%">Created</Th>
                    <Th width="6%" align="right">
                      &nbsp;
                    </Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant">
                  {rows.map((t) => (
                    <tr
                      key={t.id}
                      className="hover:bg-surface-container-low transition-colors"
                    >
                      <td className="px-4 py-3 font-body-md text-body-md text-on-surface">
                        {/* UX-025: the whole <tr> used to carry role="link"
                            with an onClick. That overrides the row's table
                            semantics for screen readers, offers none of a
                            link's affordances — no middle-click, no open in
                            new tab, no copy link address — and activated on
                            Space, which is button behaviour, not link
                            behaviour. A real anchor in the identity cell is
                            the accessible pattern and keeps the table a
                            table. */}
                        <Link
                          to={`/tenants/${t.id}`}
                          className="rounded-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                          data-testid={`tenant-row-link-${t.id}`}
                        >
                          {t.name}
                        </Link>
                      </td>
                      <td className="px-4 py-3 font-body-md text-body-md text-on-surface">
                        {t.contact_email ?? <span className="text-on-surface-variant">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md uppercase ${STATUS_TONE[t.status]}`}
                        >
                          {t.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-on-surface">
                        {t.employee_count ?? 0}
                      </td>
                      <td className="px-4 py-3 text-sm text-on-surface">
                        {t.fob_count ?? 0}
                      </td>
                      <td className="px-4 py-3 text-sm text-on-surface">
                        {t.pin_count ?? 0}
                      </td>
                      <td className="px-4 py-3 font-body-md text-body-md text-on-surface-variant">
                        {new Date(t.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <RowMenu tenant={t} onAction={(kind) => setModal({ kind, tenant: t })} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {modal ? (
        <TenantModal
          state={modal}
          onCancel={() => setModal(null)}
          onDone={(_tenant, msg) => {
            setModal(null);
            setToast(msg);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function Th({
  children,
  width,
  align,
}: {
  children: React.ReactNode;
  width?: string;
  align?: 'right';
}) {
  return (
    <th
      className={`${align === 'right' ? 'text-right' : 'text-left'} font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3`}
      style={width ? { width } : undefined}
    >
      {children}
    </th>
  );
}

function RowMenu({ tenant, onAction }: { tenant: TenantRow; onAction: (kind: ModalKind) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    function away(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    const close = () => setOpen(false);
    document.addEventListener('mousedown', away);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close);
    return () => {
      document.removeEventListener('mousedown', away);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close);
    };
  }, [open]);
  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((o) => !o);
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Row actions for ${tenant.name}`}
        className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-surface-container-high"
      >
        <Icon name="more_vert" />
      </button>
      {open ? (
        <div
          role="menu"
          style={rowMenuStyle(ref.current, 192)}
          className="bg-surface-container-lowest border border-outline-variant rounded-lg shadow-lg z-30 py-1"
        >
          <Item
            label="Edit"
            onClick={() => {
              setOpen(false);
              onAction('edit');
            }}
          />
          {tenant.status === 'active' ? (
            <Item
              label="Suspend"
              destructive
              onClick={() => {
                setOpen(false);
                onAction('suspend');
              }}
            />
          ) : null}
          {tenant.status !== 'active' ? (
            <Item
              label="Restore"
              onClick={() => {
                setOpen(false);
                onAction('restore');
              }}
            />
          ) : null}
          {tenant.status !== 'archived' ? (
            <Item
              label="Archive"
              destructive
              onClick={() => {
                setOpen(false);
                onAction('archive');
              }}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Item({
  label,
  destructive,
  onClick,
}: {
  label: string;
  destructive?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className={`w-full text-left px-3 py-2 font-body-md text-body-md hover:bg-surface-container-low ${
        destructive ? 'text-error' : 'text-on-surface'
      }`}
    >
      {label}
    </button>
  );
}

const fieldCls =
  'w-full px-3 py-2 bg-surface border border-outline-variant rounded-lg font-body-md text-body-md focus:ring-2 focus:ring-primary outline-none';

function TenantModal({
  state,
  onCancel,
  onDone,
}: {
  state: { kind: ModalKind; tenant: TenantRow | null };
  onCancel: () => void;
  onDone: (tenant: TenantRow | null, toast: string) => void;
}) {
  const { kind, tenant } = state;
  const [name, setName] = useState(tenant?.name ?? '');
  const [contact, setContact] = useState(tenant?.contact_email ?? '');
  const [contactName, setContactName] = useState(tenant?.contact_name ?? '');
  const [contactPhone, setContactPhone] = useState(tenant?.contact_phone ?? '');
  const [notes, setNotes] = useState(tenant?.notes ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const destructive = kind === 'suspend' || kind === 'archive';

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      switch (kind) {
        case 'create': {
          const res = await apiClient.post<{ data: TenantRow }>(
            '/api/v1/tenants',
            {
              name: name.trim(),
              contact_email: contact.trim() || null,
              contact_name: contactName.trim() || null,
              contact_phone: contactPhone.trim() || null,
              notes: notes.trim() || null,
            },
            { skipTenantScope: true },
          );
          onDone(res.data, `Tenant "${res.data.name}" created.`);
          return;
        }
        case 'edit': {
          await apiClient.patch(
            `/api/v1/tenants/${tenant!.id}`,
            {
              name: name.trim(),
              contact_email: contact.trim() || null,
              contact_name: contactName.trim() || null,
              contact_phone: contactPhone.trim() || null,
              notes: notes.trim() || null,
            },
            { skipTenantScope: true },
          );
          onDone(null, `Tenant "${name.trim()}" updated.`);
          return;
        }
        case 'suspend':
          await apiClient.patch(
            `/api/v1/tenants/${tenant!.id}`,
            { status: 'suspended' },
            { skipTenantScope: true },
          );
          onDone(null, `Tenant "${tenant!.name}" suspended.`);
          return;
        case 'restore':
          await apiClient.patch(
            `/api/v1/tenants/${tenant!.id}`,
            { status: 'active' },
            { skipTenantScope: true },
          );
          onDone(null, `Tenant "${tenant!.name}" restored to active.`);
          return;
        case 'archive':
          await apiClient.patch(
            `/api/v1/tenants/${tenant!.id}`,
            { status: 'archived' },
            { skipTenantScope: true },
          );
          onDone(null, `Tenant "${tenant!.name}" archived.`);
          return;
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed');
      setSubmitting(false);
    }
  }

  const heading: Record<ModalKind, string> = {
    create: 'Create tenant',
    edit: `Edit ${tenant?.name}`,
    suspend: `Suspend ${tenant?.name}`,
    restore: `Restore ${tenant?.name}`,
    archive: `Archive ${tenant?.name}`,
  };

  return (
    <div
      role="dialog"
      aria-labelledby="tenant-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-on-surface/30 p-margin-mobile"
    >
      <div className="w-full max-w-[480px] max-h-[calc(100dvh-2rem)] overflow-y-auto bg-surface-container-lowest rounded-xl shadow-lg border border-outline-variant p-8">
        <header className="flex items-center gap-3 mb-6">
          <div
            className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${
              destructive
                ? 'bg-error-container text-on-error-container'
                : 'bg-primary-container text-on-primary'
            }`}
          >
            <Icon
              name={
                kind === 'create'
                  ? 'add'
                  : kind === 'edit'
                    ? 'edit'
                    : kind === 'restore'
                        ? 'restore'
                        : kind === 'suspend'
                          ? 'pause_circle'
                          : 'inventory_2'
              }
            />
          </div>
          <h2 id="tenant-modal-title" className="font-headline-sm text-headline-sm text-on-surface">
            {/* eslint-disable-next-line security/detect-object-injection -- kind is a fixed union */}
            {heading[kind]}
          </h2>
        </header>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          {(kind === 'create' || kind === 'edit') && (
            <>
              <div className="space-y-2">
                <label
                  htmlFor="tenant-name"
                  className="font-label-md text-label-md text-on-surface block"
                >
                  Name
                </label>
                <input
                  id="tenant-name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={fieldCls}
                />
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="tenant-contact"
                  className="font-label-md text-label-md text-on-surface block"
                >
                  Contact email
                </label>
                <input
                  id="tenant-contact"
                  type="email"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  className={fieldCls}
                />
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="tenant-contact-name"
                  className="font-label-md text-label-md text-on-surface block"
                >
                  Contact name
                </label>
                <input
                  id="tenant-contact-name"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  className={fieldCls}
                />
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="tenant-contact-phone"
                  className="font-label-md text-label-md text-on-surface block"
                >
                  Phone number
                </label>
                <input
                  id="tenant-contact-phone"
                  type="tel"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  className={fieldCls}
                />
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="tenant-notes"
                  className="font-label-md text-label-md text-on-surface block"
                >
                  Notes
                </label>
                <textarea
                  id="tenant-notes"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className={fieldCls}
                />
              </div>
            </>
          )}
          {kind === 'suspend' && (
            <p className="font-body-md text-body-md text-on-surface">
              Suspending blocks portal sign-in, revokes sessions, and pauses queued access grants.
              Existing door access is unchanged — suspend or delete employees separately to revoke it.
            </p>
          )}
          {kind === 'restore' && (
            <p className="font-body-md text-body-md text-on-surface">
              Restore the tenant to <code>active</code>. Users will be able to sign in again
              immediately.
            </p>
          )}
          {kind === 'archive' && (
            <p className="font-body-md text-body-md text-on-surface">
              Archiving is the soft-delete state. The tenant disappears from default lists; only a
              master_admin can resurrect it.
            </p>
          )}
          {error ? (
            <p role="alert" className="text-error font-body-md text-body-md">
              {error}
            </p>
          ) : null}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting}>
              {kind === 'create'
                ? 'Create'
                : kind === 'edit'
                  ? 'Save'
                  : 'Confirm'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
